// Input validation utilities for enhanced security

export const validateEmail = (email: string): { isValid: boolean; error?: string } => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  if (!email) {
    return { isValid: false, error: 'Email é obrigatório' };
  }
  
  if (!emailRegex.test(email)) {
    return { isValid: false, error: 'Email inválido' };
  }
  
  if (email.length > 254) {
    return { isValid: false, error: 'Email muito longo' };
  }
  
  return { isValid: true };
};

export const validatePassword = (password: string): { isValid: boolean; error?: string; strength: 'weak' | 'medium' | 'strong' } => {
  if (!password) {
    return { isValid: false, error: 'Senha é obrigatória', strength: 'weak' };
  }
  
  if (password.length < 8) {
    return { isValid: false, error: 'Senha deve ter pelo menos 8 caracteres', strength: 'weak' };
  }
  
  if (password.length > 128) {
    return { isValid: false, error: 'Senha muito longa', strength: 'weak' };
  }
  
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  
  const strengthScore = [hasUpperCase, hasLowerCase, hasNumbers, hasSpecialChar].filter(Boolean).length;
  
  if (strengthScore < 3) {
    return { 
      isValid: false, 
      error: 'Senha deve conter pelo menos: maiúscula, minúscula, número e símbolo', 
      strength: 'weak' 
    };
  }
  
  const strength = strengthScore === 4 ? 'strong' : 'medium';
  return { isValid: true, strength };
};

export const validateAmount = (amount: string): { isValid: boolean; error?: string } => {
  if (!amount) {
    return { isValid: false, error: 'Valor é obrigatório' };
  }
  
  const numValue = parseFloat(amount);
  
  if (isNaN(numValue)) {
    return { isValid: false, error: 'Valor deve ser um número válido' };
  }
  
  if (numValue <= 0) {
    return { isValid: false, error: 'Valor deve ser maior que zero' };
  }
  
  if (numValue > 1000000000) {
    return { isValid: false, error: 'Valor muito alto' };
  }
  
  return { isValid: true };
};

export const validateDate = (date: string): { isValid: boolean; error?: string } => {
  if (!date) {
    return { isValid: false, error: 'Data é obrigatória' };
  }
  
  const dateObj = new Date(date);
  
  if (isNaN(dateObj.getTime())) {
    return { isValid: false, error: 'Data inválida' };
  }
  
  const now = new Date();
  const minDate = new Date('2020-01-01');
  const maxDate = new Date(now.getFullYear() + 10, 11, 31);
  
  if (dateObj < minDate || dateObj > maxDate) {
    return { isValid: false, error: 'Data fora do intervalo permitido' };
  }
  
  return { isValid: true };
};

export const validateDisplayName = (name: string): { isValid: boolean; error?: string } => {
  if (!name || !name.trim()) {
    return { isValid: false, error: 'Nome é obrigatório' };
  }
  
  if (name.trim().length < 2) {
    return { isValid: false, error: 'Nome deve ter pelo menos 2 caracteres' };
  }
  
  if (name.length > 100) {
    return { isValid: false, error: 'Nome muito longo' };
  }
  
  // Check for suspicious patterns
  const suspiciousPattern = /[<>{}[\]\\/"'`;=()]/;
  if (suspiciousPattern.test(name)) {
    return { isValid: false, error: 'Nome contém caracteres inválidos' };
  }
  
  return { isValid: true };
};

// Rate limiting utilities
const rateLimitStore = new Map<string, { count: number; resetTime: number }>();

export const checkRateLimit = (key: string, maxAttempts: number = 5, windowMs: number = 15 * 60 * 1000): boolean => {
  const now = Date.now();
  const existing = rateLimitStore.get(key);
  
  if (!existing || now > existing.resetTime) {
    rateLimitStore.set(key, { count: 1, resetTime: now + windowMs });
    return true;
  }
  
  if (existing.count >= maxAttempts) {
    return false;
  }
  
  existing.count++;
  return true;
};

export const getRateLimitStatus = (key: string): { remaining: number; resetTime: number } => {
  const existing = rateLimitStore.get(key);
  
  if (!existing || Date.now() > existing.resetTime) {
    return { remaining: 5, resetTime: 0 };
  }
  
  return { 
    remaining: Math.max(0, 5 - existing.count), 
    resetTime: existing.resetTime 
  };
};